/**
 * Simulação do fluxo interativo, com as mesmas regras do motor
 * (backend/src/services/interactiveCampaignFlowEngine.ts):
 * - o disparo envia os blocos em sequência até uma Condição (espera a resposta),
 *   um Encerrar ou o fim do caminho;
 * - Espera, IA, HTTP e integrações não aparecem para o contato: no preview viram avisos.
 */
import type { Edge, Node } from 'reactflow';

export type SimItem =
  | { kind: 'text'; text: string }
  | { kind: 'media'; media: 'image' | 'video' | 'audio' | 'document'; url?: string; caption?: string; fileName?: string }
  | { kind: 'buttons'; text: string; options: string[]; footer?: string }
  | { kind: 'note'; text: string; tone?: 'info' | 'warn' | 'end' };

export interface SimStep {
  items: SimItem[];
  /** Condição aguardando a resposta do contato; null = fluxo encerrado */
  waitingAt: string | null;
  /** Respostas sugeridas para a condição em espera */
  suggestions: string[];
}

export const SAMPLE_CONTACT = { nome: 'Maria', telefone: '5563999990000' };

const DELAY_UNITS: Record<string, [string, string]> = {
  seconds: ['segundo', 'segundos'], minutes: ['minuto', 'minutos'], hours: ['hora', 'horas'], days: ['dia', 'dias'],
};

export function fillVariables(text: string, vars: Record<string, string> = SAMPLE_CONTACT): string {
  return (text || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (all, key: string) => vars[key.toLowerCase()] ?? all);
}

export function conditionMatches(config: Record<string, any>, response: string): boolean {
  const operator = config.conditionType || config.operator || 'contains';
  const text = (response || '').toLowerCase().trim();
  const value = String(config.value || '').toLowerCase().trim();
  switch (operator) {
    case 'equals': case '==': return text === value;
    case 'notEquals': case '!=': return text !== value;
    case 'startsWith': return text.startsWith(value);
    case 'endsWith': return text.endsWith(value);
    case 'regex':
      try { return new RegExp(config.value || '', 'i').test(text); } catch { return false; }
    default: return text.includes(value);
  }
}

function caseMatches(c: Record<string, any>, response: string): boolean {
  const text = response.toLowerCase().trim();
  const value = String(c.value || '').toLowerCase().trim();
  switch (c.conditionType || 'equals') {
    case 'contains': return text.includes(value);
    case 'startsWith': return text.startsWith(value);
    case 'endsWith': return text.endsWith(value);
    default: return text === value;
  }
}

const YES = ['true', 'sim', 'yes', 'verdadeiro'];
const NO = ['false', 'não', 'nao', 'no', 'falso'];

/** Próximo bloco depois da resposta a uma Condição, e o caminho escolhido */
export function answerCondition(node: Node, edges: Edge[], response: string): { target?: string; path: string } {
  const config = node.data?.config || {};
  const out = edges.filter((e) => e.source === node.id);
  if (config.mode === 'switch') {
    const cases: Record<string, any>[] = config.cases || [];
    const i = cases.findIndex((c) => caseMatches(c, response));
    const edge = (i >= 0 && out.find((e) => e.sourceHandle === `case-${i}`)) || out.find((e) => e.sourceHandle === 'default') || (i < 0 ? out[0] : undefined);
    const path = i >= 0 ? (cases[i].label || cases[i].value || `Caso ${i + 1}`) : 'Padrão';
    return { target: edge?.target, path };
  }
  const met = conditionMatches(config, response);
  const words = met ? YES : NO;
  const edge = out.find((e) => e.sourceHandle && words.includes(String(e.sourceHandle).toLowerCase()))
    ?? out.find((e) => e.label != null && words.includes(String(e.label).toLowerCase()))
    ?? (out.some((e) => e.sourceHandle === 'true' || e.sourceHandle === 'false') ? undefined : out[0]);
  return { target: edge?.target, path: met ? 'Sim' : 'Não' };
}

function suggestionsFor(node: Node): string[] {
  const config = node.data?.config || {};
  if (config.mode === 'switch') return (config.cases || []).map((c: any) => String(c.value || '')).filter(Boolean).slice(0, 6);
  return config.value && config.conditionType !== 'regex' && config.conditionType !== 'variable' ? [String(config.value)] : [];
}

function describeDelay(config: Record<string, any>): string {
  const amount = Number(config.value ?? config.amount ?? config.delaySeconds ?? 0);
  const [one, many] = DELAY_UNITS[config.unit] || DELAY_UNITS.seconds;
  return amount > 0 ? `Espera de ${amount} ${amount === 1 ? one : many}: no disparo real o contato aguarda esse tempo.` : 'Espera sem tempo definido: é pulada no disparo.';
}

/** Mensagem que o bloco envia ao contato (null quando não envia nada) */
export function nodeMessage(node: Node): SimItem | null {
  const config = node.data?.config || {};
  switch (node.data?.nodeType) {
    case 'text':
    case 'action': {
      const variations: string[] = (config.textVariations || []).filter((t: string) => t?.trim());
      const text = variations[0] || config.content || config.message || '';
      return text.trim() ? { kind: 'text', text: fillVariables(text) } : { kind: 'note', tone: 'warn', text: 'Bloco Texto sem mensagem.' };
    }
    case 'image':
    case 'video':
    case 'audio':
    case 'document':
      return { kind: 'media', media: node.data.nodeType, url: config.mediaUrl || undefined, caption: config.caption ? fillVariables(config.caption) : undefined, fileName: config.fileName };
    case 'button': {
      const options = (config.buttons || []).map((b: any) => String(b.text || '').trim()).filter(Boolean);
      return { kind: 'buttons', text: fillVariables(config.bodyText || 'Escolha uma opção:'), options, footer: config.footer || undefined };
    }
    default:
      return null;
  }
}

const SILENT: Record<string, string> = {
  ai: 'Bloco IA: gera a resposta no disparo real.',
  httprest: 'Requisição HTTP: chamada no disparo real.',
  integration_perfex: 'Perfex CRM: atualizado no disparo real.',
  integration_chatwoot: 'Chatwoot: conversa enviada ao atendimento no disparo real.',
  sequence_enroll: 'Sequência: contato inscrito no disparo real.',
  meta_conversion: 'Conversão Meta Ads: enviada no disparo real (só para quem veio de anúncio).',
};

/** Percorre o fluxo a partir de `fromId` (exclusivo) até precisar de uma resposta */
export function runFrom(fromId: string, nodes: Node[], edges: Edge[], startAt?: string): SimStep {
  const items: SimItem[] = [];
  const visited = new Set<string>();
  let nextId: string | undefined = startAt ?? edges.find((e) => e.source === fromId)?.target;

  for (;;) {
    if (!nextId) {
      items.push({ kind: 'note', tone: 'end', text: 'Fim do fluxo.' });
      return { items, waitingAt: null, suggestions: [] };
    }
    const node = nodes.find((n) => n.id === nextId);
    if (!node) {
      items.push({ kind: 'note', tone: 'warn', text: 'Ligação aponta para um bloco que não existe mais.' });
      return { items, waitingAt: null, suggestions: [] };
    }
    if (visited.has(node.id)) {
      items.push({ kind: 'note', tone: 'warn', text: 'O fluxo volta para um bloco já enviado: no disparo real ele para aqui.' });
      return { items, waitingAt: null, suggestions: [] };
    }
    visited.add(node.id);
    const type = node.data?.nodeType;

    if (type === 'condition') {
      const config = node.data?.config || {};
      const hasRule = config.mode === 'switch' ? (config.cases || []).length > 0 : Boolean(config.value);
      if (!hasRule) items.push({ kind: 'note', tone: 'warn', text: 'Condição sem valor configurado.' });
      return { items, waitingAt: node.id, suggestions: suggestionsFor(node) };
    }
    if (type === 'stop') {
      items.push({ kind: 'note', tone: 'end', text: 'Fluxo encerrado.' });
      return { items, waitingAt: null, suggestions: [] };
    }
    if (type === 'delay') items.push({ kind: 'note', text: describeDelay(node.data?.config || {}) });
    else if (SILENT[type]) items.push({ kind: 'note', text: SILENT[type] });
    else {
      const msg = nodeMessage(node);
      if (msg) items.push(msg);
    }
    nextId = edges.find((e) => e.source === node.id)?.target;
  }
}

/** Início do fluxo: primeiro bloco ligado ao Início */
export function startFlow(nodes: Node[], edges: Edge[]): SimStep {
  const trigger = nodes.find((n) => n.data?.nodeType === 'trigger');
  if (!trigger) return { items: [{ kind: 'note', tone: 'warn', text: 'Adicione o bloco Início ao fluxo.' }], waitingAt: null, suggestions: [] };
  if (!edges.some((e) => e.source === trigger.id)) {
    return { items: [{ kind: 'note', tone: 'warn', text: 'Ligue o bloco Início ao primeiro bloco do fluxo.' }], waitingAt: null, suggestions: [] };
  }
  return runFrom(trigger.id, nodes, edges);
}
